import { ValidatorResult } from '../validator';

import { FileType } from './server';
import {
  mapFileExtensionToFileType,
  mapExactMimeTypeToFileType,
  mapFileTypeToLabel,
  browserPlayableAudioExtensions,
  browserPlayableVideoExtensions
} from './client';

export const downloadFile = ({
  blob,
  filename
}: {
  blob: Blob;
  filename: string;
}): void => {
  // @ts-ignore ie support
  if (typeof window.navigator.msSaveBlob !== 'undefined') {
    // IE doesn't allow using a blob object directly as link href.
    // Workaround for "HTML7007: One or more blob URLs were
    // revoked by closing the blob for which they were created.
    // These URLs will no longer resolve as the data backing
    // the URL has been freed."
    // @ts-ignore ie support
    window.navigator.msSaveBlob(blob, filename);
    return;
  }
  // Other browsers
  // Create a link pointing to the ObjectURL containing the blob
  const blobURL = window.URL.createObjectURL(blob);
  const tempLink = document.createElement('a');
  tempLink.style.display = 'none';
  tempLink.href = blobURL;
  tempLink.setAttribute('download', filename);
  // Safari thinks _blank anchor are pop ups. We only want to set _blank
  // target if the browser does not support the HTML5 download attribute.
  // This allows you to download files in desktop safari if pop up blocking
  // is enabled.
  if (typeof tempLink.download === 'undefined') {
    tempLink.setAttribute('target', '_blank');
  }
  document.body.appendChild(tempLink);
  tempLink.click();
  document.body.removeChild(tempLink);
  setTimeout(() => {
    // For Firefox it is necessary to delay revoking the ObjectURL
    window.URL.revokeObjectURL(blobURL);
  }, 100);
};

const triggerUrlDownload = (url: string, filename: string): void => {
  const tempLink = document.createElement('a');
  tempLink.style.display = 'none';
  tempLink.href = url;
  tempLink.setAttribute('download', filename);
  tempLink.rel = 'noopener noreferrer';
  tempLink.target = '_blank';
  document.body.appendChild(tempLink);
  tempLink.click();
  document.body.removeChild(tempLink);
};

export const downloadFileFromUrl = async ({
  url,
  filename
}: {
  url: string;
  filename: string;
}): Promise<void> => {
  try {
    const response = await fetch(url, { credentials: 'include' });

    if (!response.ok) {
      throw new Error(`Download failed: ${response.status}`);
    }

    const blob = await response.blob();
    downloadFile({ blob, filename });
  } catch {
    triggerUrlDownload(url, filename);
  }
};

export const getFileExtensionFromName = (name: string): string | null => {
  const FILE_NAME_REGEX = /^.+\.(?<extension>.+)$/;

  const matchGroups = name.match(FILE_NAME_REGEX)?.groups;

  if (!matchGroups || !matchGroups.extension) {
    return null;
  }

  return !matchGroups || !matchGroups.extension ? null : matchGroups.extension;
};

export const getFileExtensionFromUrl = (
  url: string | null | undefined
): string | null => {
  if (!url) {
    return null;
  }

  try {
    const pathname = new URL(url).pathname;

    return getFileExtensionFromName(pathname);
  } catch {
    return getFileExtensionFromName(url);
  }
};

export const getFileTypeFromName = (name: string): FileType | null => {
  const extension = getFileExtensionFromName(name);

  if (!extension) {
    return null;
  }

  return mapFileExtensionToFileType[extension] || FileType.other;
};

export const getFileTypeFromMimeType = (mimeType: string): FileType | null => {
  const mime = mimeType.toLowerCase().trim();

  if (!mime) {
    return null;
  }

  if (mime.startsWith('image/')) {
    return FileType.image;
  }

  if (mime.startsWith('video/')) {
    return FileType.video;
  }

  if (mime.startsWith('audio/')) {
    if (mime.includes('ogg')) {
      return FileType.voice;
    }

    return FileType.music;
  }

  return mapExactMimeTypeToFileType[mime] ?? null;
};

export const isAudioFileType = (fileType: FileType): boolean =>
  fileType === FileType.voice || fileType === FileType.music;

/** Получаем тип файла по имени или, если не смогли получить по имени, то по MIME-типу. */
export const getFileTypeFromFile = (file: File): FileType | null => {
  return getFileTypeFromName(file.name) ?? getFileTypeFromMimeType(file.type);
};

/** Получаем файлы из DataTransfer (вставка из буфера обмена, etc.). Возвращает null, если нет файлов. */
export const getFilesFromDataTransfer = (
  dataTransfer: DataTransfer
): File[] | null => {
  const files: File[] = [];

  Array.from(dataTransfer.items).forEach((item) => {
    if (item.kind !== 'file') {
      return;
    }

    const file = item.getAsFile();

    if (file) {
      files.push(file);
    }
  });

  return files.length ? files : null;
};

/** Получаем тип файла по расширению без учета регистра */
export const resolveFileTypeByExtension = (extension: string): FileType =>
  mapFileExtensionToFileType[extension.toLowerCase()] || FileType.other;

export const validateFileExtension = ({
  file,
  expectedExtensions
}: {
  file: File;
  expectedExtensions: string[];
}): ValidatorResult => {
  const extension = getFileExtensionFromName(file.name);

  if (!extension) {
    return (t) =>
      t('validators.errors.fileInvalidExtension', {
        ns: 'entities'
      });
  }

  return expectedExtensions.includes(extension)
    ? null
    : (t) =>
        t('validators.errors.fileInvalidExtension', {
          ns: 'entities'
        });
};

const MAX_SIZE = 50 * 1024 * 1024;

/**
 * Валидирует размер файла
 * @param size - размер в байтах
 * @return {"Файл превыщает размер 50 MB" | null}
 */
export const validateFileMaxSize = (size: number): ValidatorResult => {
  return size > MAX_SIZE
    ? (t) =>
        t('validators.errors.fileMaxSize', {
          ns: 'entities'
        })
    : null;
};

/**
 * Маппинг FileType к MIME-типам для атрибута accept
 */
const fileTypeToAcceptMap: Record<FileType, string> = {
  [FileType.all]: '*/*',
  [FileType.image]: 'image/*',
  [FileType.video]: 'video/*',
  [FileType.music]: 'audio/*',
  [FileType.voice]: 'audio/*',
  [FileType.doc]: '.doc,.docx,.pdf,.txt,.rtf,.odt',
  [FileType.table]: '.xls,.xlsx,.csv,.ods',
  [FileType.sticker]: 'image/png,image/webp',
  [FileType.site]: '.html,.htm',
  [FileType.other]: '*/*'
};

/**
 * Генерирует строку accept для input[type="file"] на основе массива FileType
 * @param fileTypes - массив типов файлов
 * @return строка accept для атрибута input или undefined если нет ограничений
 */
export const generateAcceptFromFileTypes = (
  fileTypes: FileType[] | null
): string | undefined => {
  if (
    !fileTypes ||
    fileTypes.length === 0 ||
    fileTypes.includes(FileType.all)
  ) {
    return undefined;
  }

  // Собираем уникальные MIME-типы
  const acceptValues = Array.from(
    new Set(fileTypes.map((type) => fileTypeToAcceptMap[type]))
  );

  return acceptValues.join(',');
};

const getDocumentLabel = ({
  extension,
  url
}: {
  extension?: string | null;
  url?: string | null;
}): string => {
  const resolvedExtension = (
    extension ||
    getFileExtensionFromUrl(url) ||
    ''
  ).toLowerCase();

  if (resolvedExtension === 'pdf') {
    return 'PDF';
  }

  // doc/docx и пустое → DOC; svg/txt/… — по расширению
  if (
    !resolvedExtension ||
    resolvedExtension === 'doc' ||
    resolvedExtension === 'docx'
  ) {
    return mapFileTypeToLabel[FileType.doc];
  }

  return resolvedExtension.toUpperCase().slice(0, 4);
};

export const getFileLabel = ({
  extension,
  url,
  type
}: {
  extension?: string | null;
  url?: string | null;
  type: FileType;
}): string => {
  if (type === FileType.doc) {
    return getDocumentLabel({ extension, url });
  }

  const actualExtension =
    extension || getFileExtensionFromUrl(url) || mapFileTypeToLabel[type];

  return actualExtension.toUpperCase().slice(0, 4);
};

export const isBrowserPlayableVideoExtension = (extension: string): boolean =>
  browserPlayableVideoExtensions.includes(extension.toLowerCase());

export const isBrowserPlayableAudioExtension = (extension: string): boolean =>
  browserPlayableAudioExtensions.includes(extension.toLowerCase());

export const getResolvedFileExtension = ({
  extension,
  url
}: {
  extension?: string | null;
  url?: string | null;
}): string | null => extension || getFileExtensionFromUrl(url);

export const isBrowserPlayableAudio = ({
  extension,
  url,
  fallbackToAudioWithoutExtension = false
}: {
  extension?: string | null;
  url?: string | null;
  fallbackToAudioWithoutExtension?: boolean;
}): boolean => {
  const resolvedExtension = getResolvedFileExtension({ extension, url });

  if (!resolvedExtension) {
    return fallbackToAudioWithoutExtension;
  }

  return isBrowserPlayableAudioExtension(resolvedExtension);
};
